import { resolvePurchaseDiscount, isAutomaticPurchase } from './purchase-discount.js';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';
import { getPrimaryDiscountCodeFromUrl } from '@theme/free-processing-discount';
import { formatMoney } from '@theme/money-formatting';
import { resolveDiscountQuote } from './discount-pricing.js';
import { lookupDisplayOffer, resolveAdvertisedSavings } from './discount-display.js';

const SITEWIDE_DISCOUNT_PRICE_EXPOSURE_EVENT = 'legacybox_sitewide_discount_price_exposure';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  #isProductCard = false;
  #abortController = new AbortController();
  #quoteExpiryTimer;
  #offerRevision = 0;
  #offerExpiryTimer;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;

    this.#isProductCard = Boolean(this.dataset.productCard || this.closest('product-card'));

    this.#refreshDiscountState();
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice, { signal });
    document.addEventListener(ThemeEvents.discountUpdate, this.#refreshDiscountState, { signal });
    document.addEventListener('shopify:section:load', this.#refreshDiscountState, { signal });
    document.addEventListener('shopify:section:unload', this.#refreshDiscountState, { signal });
    window.addEventListener('pageshow', this.#refreshDiscountState, { signal });
    document.addEventListener('visibilitychange', this.#handleVisibilityChange, { signal });
  }

  disconnectedCallback() {
    this.#offerRevision++;
    clearTimeout(this.#offerExpiryTimer);
    clearTimeout(this.#quoteExpiryTimer);
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (
      event.target instanceof HTMLElement &&
      event.target.dataset.productId !== this.dataset.productId
    ) {
      return;
    }

    const { priceContainer, volumePricingNote } = this.refs;
    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');

    this.addDiscountPrice(newPrice);
    this.addFreeShippingBadge(newPrice);

    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      // Use newPrice since priceContainer was just replaced and now points to the detached element
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }
  };

  #handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      this.#refreshDiscountState();
    }
  };

  #refreshDiscountState = () => {
    const price = this.querySelector('[ref="priceContainer"]');

    this.addDiscountPrice(price);
    this.addFreeShippingBadge(price);
    clearTimeout(this.#quoteExpiryTimer);
    const data = this.#purchaseDiscount();
    const expires = Date.parse(data?.displayPricing?.expiresAt);
    const delay = expires - Date.now();
    if (Number.isFinite(delay) && delay > 0) {
      this.#quoteExpiryTimer = setTimeout(() => {
        this.#refreshDiscountState();
        document.dispatchEvent(new CustomEvent('legacybox:discount-quote-expired'));
      }, Math.min(delay + 1, 2147483647));
    }
  };

  /**
   * Add/Remove discount price
   * @param {Element | null} element
   */
  addDiscountPrice(element) {
    if (!element) return;
    const exclusionBadge = element.querySelector('[data-kit-exclusion-badge]');
    const offerRevision = ++this.#offerRevision;
    clearTimeout(this.#offerExpiryTimer);

    const price = Number(element.getAttribute('data-price'));
    const productId = element.getAttribute('data-product-id');
    const variantId = element.getAttribute('data-variant-id');

    if (this.#isProductCard && !this.#isAutoApplyAllowed(price)) {
      this.#removeDiscountElements(element);
      return;
    }

    const resolved = this.#resolveDiscount(variantId, productId, price);

    if (!resolved) {
      this.#removeDiscountElements(element);
      void this.#showSitewideOffer(element, offerRevision);
      return;
    }

    const previousPrice = element.querySelector('.discount-price')?.innerText;
    const previousBadge = element.querySelector('.discount-badge')?.innerText;
    const previousEffectivePrice = element.getAttribute('data-effective-price');
    if (exclusionBadge && !exclusionBadge.hidden) exclusionBadge.hidden = true;
    const { discountedPrice, badgeText, priceCents, dollarSavings } = this.#computeDiscount(resolved, price);
    if (previousEffectivePrice !== String(priceCents ?? price)) element.setAttribute('data-effective-price', String(priceCents ?? price));
    element.toggleAttribute('data-dollar-savings', Boolean(dollarSavings));

    if (discountedPrice == null) {
      this.#removeDiscountElements(element);
      return;
    }

    const priceEl = /** @type {HTMLElement} */ (
      element.querySelector('.discount-price') ?? document.createElement('div')
    );
    if (priceEl.className !== 'discount-price') priceEl.className = 'discount-price';
    if (priceEl.innerText !== discountedPrice) priceEl.innerText = discountedPrice;
    if (!element.querySelector('.discount-price')) (element.querySelector('[data-price-values]') ?? element).append(priceEl);

    let imageBadgeChanged = false;
    if (this.hasAttribute('data-image-savings-badge')) {
      element.querySelector('.discount-badge')?.remove();
      imageBadgeChanged = this.#updateImageSavingsBadge(badgeText ? price - (priceCents ?? price) : 0,
        resolved.source === 'override' && resolved.override.campaign_display === true);
    } else if (badgeText) {
      const badgeEl = /** @type {HTMLElement} */ (
        element.querySelector('.discount-badge') ?? document.createElement('div')
      );
      if (badgeEl.className !== 'discount-badge') badgeEl.className = 'discount-badge';
      if (badgeEl.innerText !== badgeText) badgeEl.innerText = badgeText;
      if (!element.querySelector('.discount-badge')) (element.querySelector('[data-price-badges]') ?? element).append(badgeEl);
    }

    if (previousPrice !== discountedPrice || previousBadge !== element.querySelector('.discount-badge')?.innerText ||
        imageBadgeChanged || previousEffectivePrice !== String(priceCents ?? price)) {
      element.dispatchEvent(new CustomEvent('legacybox:price-rendered', { bubbles: true }));
    }
    this.#trackSitewideDiscountPriceExposure(element, resolved, {
      badgeText,
      discountedPrice,
      price,
      productId,
      variantId,
    });
  }

  /**
   * Check is Auto Apply discount allowed for product card
   * @param {Number} price
   */
  #isAutoApplyAllowed(price) {
    const config = Theme.legacyBox.discounts;
    const isAllow = config.allow && config.sitewide.strikethrough;

    if (!isAllow) return false;

    if (config.sitewide.threshold.active) {
      return price > config.sitewide.threshold.amount;
    }

    return true;
  }

  // Link-only regular-kit cards describe the next PDP purchase, independently of
  // a bundle already in the cart. Liquid opts in only verified regular variants.
  #isRegularKitCard() {
    return this.hasAttribute('data-regular-kit-purchase') ||
      Boolean(this.closest('product-card[data-regular-kit-purchase]'));
  }

  #purchaseTemplate() {
    return this.#isRegularKitCard() ? 'product' :
      this.closest('[data-purchase-template]')?.dataset.purchaseTemplate || Theme.template?.name;
  }

  #purchaseConfig() {
    const config = Theme.legacyBox.discounts;
    // Section-level bundle offers belong to this page's purchase form. Only
    // shared campaign display rules also apply to a card linking to another PDP.
    return this.#isRegularKitCard()
      ? { ...config, overrides: config.overrides.filter(offer => offer.campaign_display) }
      : config;
  }

  #purchaseDiscount() {
    const config = this.#purchaseConfig();
    const applied = this.#readSession(config.sessionKey);
    return isAutomaticPurchase(config, applied, this.#purchaseTemplate(), this.#getUrlDiscountCode())
      ? resolvePurchaseDiscount(config, applied, this.#readSession(config.defaultSessionKey),
        this.#purchaseTemplate(), this.#getUrlDiscountCode()) : applied;
  }

  /**
   * Resolve the best-matching discount for a variant/product. Cascade is:
   *   1. Explicit override mapped to this variant; BOGO overrides require an active/requested code
   *   2. Currently-applied cart discount, if it can render for this card
   *   3. Auto-apply fallback (default_discount), if it can render for this card
   *
   * @param {string | null} variantId
   * @param {string | null} productId
   * @param {number} price
   * @returns {{ source: 'override', override: any } | { source: 'applied' | 'fallback', data: any } | { source: 'quote', data: any, priceCents: number, savingsCents: number } | null}
   */
  #resolveDiscount(variantId, productId, price) {
    const applied = this.#purchaseDiscount();
    const requestedCode = this.#getUrlDiscountCode();
    const config = this.#purchaseConfig();
    if (!applied && isAutomaticPurchase(config, this.#readSession(config.sessionKey),
      this.#purchaseTemplate(), requestedCode)) return null;
    if (requestedCode && requestedCode !== this.#getSessionDiscountCode(applied)) return null;
    // A supplied quote is authoritative, including an expired/ineligible response.
    // Manual kit amounts are used only when the service has no quote contract.
    if (applied?.displayPricing !== undefined) {
      const quote = resolveDiscountQuote(applied, { productId, variantId, currency: Shopify.currency.active, price, context: Theme.legacyBox.discounts.quoteContext });
      return quote && Theme.legacyBox.discounts.allow ? { source: 'quote', data: applied, ...quote } : null;
    }
    const override = variantId
      ? config.overrides.find((o) => {
          return o.variant_id.includes(`${variantId}`) && this.#isOverrideDiscountActive(o);
        })
      : null;
    if (override) return { source: 'override', override };

    if (this.#isRenderableDiscount(applied, productId)) {
      return { source: 'applied', data: applied };
    }

    // An explicit code must not fall back to another campaign's cached estimate.
    if (applied && applied.applied !== 'auto_apply') return null;
    const fallback = this.#readSession(Theme.legacyBox.discounts.defaultSessionKey);
    if (applied && this.#getSessionDiscountCode(applied) !== this.#getSessionDiscountCode(fallback)) return null;
    if (fallback?.displayPricing !== undefined) return null;
    if (this.#isRenderableDiscount(fallback, productId)) {
      return { source: 'fallback', data: fallback };
    }

    return null;
  }

  /**
   * @returns {string | null}
   */
  #getUrlDiscountCode() {
    return getPrimaryDiscountCodeFromUrl();
  }

  /**
   * @param {any} data
   * @returns {string | null}
   */
  #getSessionDiscountCode(data) {
    const code = data?.code;
    return typeof code === 'string' && code.trim() ? code.trim().toUpperCase() : null;
  }

  /**
   * @param {any} override
   */
  #isOverrideDiscountActive(override) {
    if (override?.additive) return false;
    if (!override?.is_bogo && !override?.require_code) return true;
    if (override.require_code && override.type === 'amount_off' && Shopify.currency.active !== 'USD') return false;

    const overrideCode =
      typeof override?.auto_discount === 'string'
        ? override.auto_discount.trim().toUpperCase()
        : '';

    if (!overrideCode) return false;

    const applied = this.#purchaseDiscount();
    const requestedCode = this.#getUrlDiscountCode();
    if (override.require_code) {
      return applied?.status === 'success' && this.#getSessionDiscountCode(applied) === overrideCode &&
        (!requestedCode || requestedCode === overrideCode);
    }
    return (requestedCode || this.#getSessionDiscountCode(applied)) === overrideCode;
  }

  /**
   * @param {string} key
   */
  #readSession(key) {
    if (!key) return null;
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  /**
   * @param {any} data
   * @param {string | null} productId
   */
  #isRenderableDiscount(data, productId) {
    return this.#productIsInScope(data, productId) && this.#hasDisplayableValue(data);
  }

  /**
   * @param {any} data
   */
  #hasDisplayableValue(data) {
    const value = data?.customerGets?.value;
    const percentage = Number(value?.percentage);
    const amount = Number(value?.amount?.amount);
    if (Number.isFinite(percentage) && percentage > 0 && percentage <= 1) return true;
    // An order-level or shared fixed discount cannot be subtracted from every kit.
    return Number.isFinite(amount) && amount > 0 && value?.appliesOnEachItem === true &&
      data.discountClasses?.includes('PRODUCT') && Shopify.currency.active === 'USD';
  }

  /**
   * DiscountCodeApp / Shopify Functions payloads have no customerGets — we
   * can't enumerate their scope client-side, so we assume eligibility.
   *
   * @param {any} data
   * @param {string | null} productId
   */
  #productIsInScope(data, productId) {
    if (!data || data.status === 'fail') return false;
    const items = data.customerGets?.items;
    if (!items) return true;
    if (items.allItems) return true;
    if (!productId) return false;
    return !!items.products?.nodes?.some(
      /** @param {{ id: string }} n */ (n) => n.id.includes(productId)
    );
  }

  /**
   * Compute price + badge text from the resolved discount source.
   * @param {{ source: 'override', override: any } | { source: 'applied' | 'fallback', data: any } | { source: 'quote', data: any, priceCents: number, savingsCents: number }} resolved
   * @param {number} price
   */
  #computeDiscount(resolved, price) {
    if (resolved.source === 'quote') {
      return {
        dollarSavings: true,
        priceCents: resolved.priceCents,
        discountedPrice: formatMoney(resolved.priceCents, Theme.format, Shopify.currency.active),
        badgeText: `Save ${this.#formatSavings(resolved.savingsCents)}`,
      };
    }
    /** @type {any} */
    let valueData;
    /** @type {any} */
    let override;

    if (resolved.source === 'override') {
      override = resolved.override;
      valueData =
        override.type === 'percentage'
          ? { percentage: override.percentage }
          : { amount: { amount: override.amount_off } };
    } else {
      valueData = resolved.data.customerGets?.value;
    }

    if (!valueData) {
      return { discountedPrice: null, badgeText: null };
    }

    const isPercent = Boolean(valueData.percentage);
    const fixedAmount = Number(valueData.amount?.amount);
    const fixedAmountCents = Number.isFinite(fixedAmount) ? Math.round(fixedAmount * 100) : 0;
    const appliedDiscount = isPercent
      ? price * valueData.percentage
      : Math.min(price, fixedAmountCents);
    const rawDiscounted = price - appliedDiscount;

    let rounded;
    if (override?.price_type === 'manual' && override.price !== 0) {
      rounded = Math.max(0, override.price);
    } else {
      rounded = Number.isInteger(rawDiscounted) ? rawDiscounted : Math.ceil(rawDiscounted);
      rounded = Math.max(0, rounded);
    }

    const formattedPrice = formatMoney(rounded, Theme.format, Shopify.currency.active);

    const baseBadgeText = override?.is_bogo
      ? 'BOGO'
      : `Save ${
          isPercent
            ? this.#formatPercentage(valueData.percentage)
            : this.#formatSavings(price - rounded, true, override?.campaign_display === true)
        }`;
    const badgeCustomText = override?.badge_prefix?.trim();
    const badgeTextEnabled = override?.badge_text_enabled ?? Boolean(badgeCustomText);
    const badgeTextType = override?.badge_text_type ?? 'prefix';

    let badgeText = baseBadgeText;

    if (badgeTextEnabled && badgeCustomText) {
      badgeText =
        badgeTextType === 'suffix'
          ? `${baseBadgeText} ${badgeCustomText}`
          : `${badgeCustomText} ${baseBadgeText}`;
    }

    return { discountedPrice: formattedPrice, badgeText, priceCents: rounded, dollarSavings: !isPercent && !override?.is_bogo };
  }

  /** Round campaign badge copy independently of the effective purchase price.
   * @param {number} savingsCents
   * @param {boolean} trimZeros
   * @param {boolean} roundUp
   */
  #formatSavings(savingsCents, trimZeros = true, roundUp = false) {
    if (roundUp && Shopify.currency.active === 'USD') savingsCents = Math.ceil(savingsCents / 100) * 100;
    let format = Theme.format;
    if (trimZeros && Shopify.currency.active === 'USD' && savingsCents % 100 === 0) {
      format = format.replace(/{{\s*(amount|amount_with_comma_separator|amount_with_space_separator)\s*}}/g, (_, key) => {
        const formats = {
          amount: 'amount_no_decimals',
          amount_with_comma_separator: 'amount_no_decimals_with_comma_separator',
          amount_with_space_separator: 'amount_no_decimals_with_space_separator',
        };
        return `{{${formats[key]}}}`;
      });
    }
    return formatMoney(savingsCents, format, Shopify.currency.active);
  }

  /**
   * @param {number | string} value
   * @returns {string}
   */
  #formatPercentage(value) {
    const percentage = Number(value) * 100;
    if (!Number.isFinite(percentage)) return '0%';

    return `${Math.round(percentage)}%`;
  }

  /**
   * @returns {Set<string>}
   */
  #getSitewideDiscountExposureRegistry() {
    window.Legacybox = window.Legacybox || {};
    window.Legacybox.sitewideDiscountPriceExposureKeys =
      window.Legacybox.sitewideDiscountPriceExposureKeys || new Set();

    return window.Legacybox.sitewideDiscountPriceExposureKeys;
  }

  /**
   * @param {{ source: 'override', override: any } | { source: 'applied' | 'fallback', data: any } | { source: 'quote', data: any, priceCents: number, savingsCents: number }} resolved
   */
  #getSitewideDiscountTestState(resolved) {
    if (resolved.source === 'override' || resolved.source === 'quote') return null;

    const sessionState = resolved.data?.sitewideDiscountAbTest;
    const liveState = window.Legacybox?.sitewideDiscountAbTest;
    const isVerifiedState = (state) => {
      return Boolean(
        state?.targetTestId &&
        state?.assignedHypothesisId &&
        state.source !== 'ineligible' &&
        state.eligible !== false
      );
    };
    const isVerifiedSessionState = (state) => {
      return isVerifiedState(state) && state.assignmentVerified === true;
    };

    if (isVerifiedSessionState(sessionState)) {
      if (
        isVerifiedState(liveState) &&
        liveState.targetTestId === sessionState.targetTestId &&
        liveState.assignedHypothesisId === sessionState.assignedHypothesisId
      ) {
        return liveState;
      }

      return sessionState;
    }

    if (isVerifiedState(liveState) && liveState.targetTestId === sessionState?.targetTestId) {
      return liveState;
    }

    return null;
  }

  /**
   * @param {Element} element
   * @param {{ source: 'override', override: any } | { source: 'applied' | 'fallback', data: any } | { source: 'quote', data: any, priceCents: number, savingsCents: number }} resolved
   * @param {{ badgeText: string | null, discountedPrice: string, price: number, productId: string | null, variantId: string | null }} exposure
   */
  #trackSitewideDiscountPriceExposure(element, resolved, exposure) {
    const state = this.#getSitewideDiscountTestState(resolved);
    const discountCode =
      resolved.source === 'override' ? null : this.#getSessionDiscountCode(resolved.data);

    if (!state?.targetTestId || !state?.bucket || !discountCode) return;
    if (state.source === 'ineligible' || state.eligible === false || !state.assignedHypothesisId) {
      return;
    }

    const key = [
      state.targetTestId,
      state.bucket,
      discountCode,
      exposure.productId || '',
      exposure.variantId || '',
    ].join(':');
    const registry = this.#getSitewideDiscountExposureRegistry();

    if (registry.has(key)) return;

    if (element.getAttribute('data-sitewide-discount-price-exposure-key') === key) return;

    element.setAttribute('data-sitewide-discount-price-exposure-key', key);

    const publishExposure = () => {
      if (registry.has(key)) return;

      registry.add(key);
      element.removeAttribute('data-sitewide-discount-price-exposure-key');

      const payload = {
        event: SITEWIDE_DISCOUNT_PRICE_EXPOSURE_EVENT,
        test_name: state.testName || 'sitewide-discount-code-v1',
        test_id: state.targetTestId,
        bucket: state.bucket,
        discount_code: discountCode,
        assigned_hypothesis_id: state.assignedHypothesisId || null,
        assignment_source: state.source || null,
        assignment_reason: state.reason || null,
        price_source: resolved.source,
        product_id: exposure.productId,
        variant_id: exposure.variantId,
        price_cents: exposure.price,
        discounted_price: exposure.discountedPrice,
        badge_text: exposure.badgeText,
        page_path: window.location.pathname,
      };

      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push(payload);
      }

      document.dispatchEvent(
        new CustomEvent('legacybox:sitewide-discount-price-exposure', {
          detail: payload,
        })
      );
    };

    if (!('IntersectionObserver' in window)) {
      publishExposure();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        observer.disconnect();
        publishExposure();
      },
      { threshold: 0.1 }
    );

    observer.observe(element);
  }

  /**
   * Update the editable badge without replacing its Shopify block wrapper.
   * @param {number} savingsCents
   * @param {boolean} roundUp
   * @returns {boolean} Whether visible badge state changed.
   */
  #updateImageSavingsBadge(savingsCents, roundUp = false) {
    if (!this.hasAttribute('data-image-savings-badge')) return false;
    const badge = this.closest('.memory-landing__product-card')?.querySelector('[data-savings-badge]');
    if (!(badge instanceof HTMLElement)) return false;
    const text = badge.querySelector('[data-savings-badge-text]');
    if (!text) return false;

    const visible = badge.dataset.enabled === 'true' && Number.isFinite(savingsCents) && savingsCents > 0;
    let label = '';
    if (visible) {
      label = `${badge.dataset.label || ''} ${this.#formatSavings(savingsCents, badge.dataset.trimZeros === 'true', roundUp)}`.trim();
    }
    const changed = badge.hidden !== !visible || text.textContent !== label;
    if (badge.hidden !== !visible) badge.hidden = !visible;
    if (text.textContent !== label) text.textContent = label;
    return changed;
  }

  /** An alternate campaign supplies badge savings without changing the applied price. */
  async #showSitewideOffer(element, revision) {
    const config = Theme.legacyBox.discounts;
    if (!this.hasAttribute('data-image-savings-badge') ||
        !config.allow || !config.autoApply?.active) return;
    const badge = this.closest('.memory-landing__product-card')?.querySelector('[data-savings-badge]');
    if (badge?.dataset.enabled !== 'true' || badge.dataset.sitewideOffer !== 'true') return;
    const code = config.autoApply.code?.trim().toUpperCase();
    if (!code) return;
    const applied = this.#readSession(config.sessionKey);
    // Advertise an alternative only for an explicit, different campaign. Do not
    // disguise loading/failed automatic application as an offer-only price.
    if (this.#getUrlDiscountCode() || applied?.status !== 'success' || applied.applied === 'auto_apply' ||
        !this.#getSessionDiscountCode(applied) || this.#getSessionDiscountCode(applied) === code) return;
    const data = await lookupDisplayOffer(config, code);
    if (revision !== this.#offerRevision || !this.isConnected) return;
    const savings = resolveAdvertisedSavings(config, data, {
      code, productId: element.getAttribute('data-product-id'), variantId: element.getAttribute('data-variant-id'),
      currency: Shopify.currency.active, price: Number(element.getAttribute('data-price')),
    });
    const roundUp = !data?.displayPricing && config.overrides?.some(override =>
      override.campaign_display === true && override.auto_discount?.trim().toUpperCase() === code &&
      override.variant_id?.includes(element.getAttribute('data-variant-id')));
    this.#updateImageSavingsBadge(savings || 0, Boolean(roundUp));
    // Clear/re-evaluate visible offers before their metadata/quote can become stale.
    const quoteExpiry = Date.parse(data?.displayPricing?.expiresAt);
    const delay = Number.isFinite(quoteExpiry) ? Math.min(60000, quoteExpiry - Date.now()) : 60000;
    if (savings && delay > 0) this.#offerExpiryTimer = setTimeout(this.#refreshDiscountState, delay + 1);
  }

  /**
   * Remove discount price.
   * @param {Element} element
   */
  #removeDiscountElements(element) {
    const hadDiscount = Boolean(element.querySelector('.discount-price') || element.querySelector('.discount-badge'));
    const price = element.getAttribute('data-price') || '0';
    const priceChanged = element.getAttribute('data-effective-price') !== price;
    this.#updateImageSavingsBadge(0);
    element.removeAttribute('data-dollar-savings');
    if (priceChanged) element.setAttribute('data-effective-price', price);
    element.querySelector('.discount-price')?.remove();
    element.querySelector('.discount-badge')?.remove();
    this.#updateKitExclusionBadge(element);
    if (hadDiscount || priceChanged) element.dispatchEvent(new CustomEvent('legacybox:price-rendered', { bubbles: true }));
  }

  /** Preserve the native editor block; never label other campaigns as excluded. */
  #updateKitExclusionBadge(element) {
    const badge = element.querySelector('[data-kit-exclusion-badge]');
    if (!badge) return;
    const config = Theme.legacyBox.discounts;
    const applied = this.#purchaseDiscount();
    const requested = this.#getUrlDiscountCode();
    badge.hidden = !(config.allow && badge.getAttribute('data-enabled') === 'true' &&
      badge.getAttribute('data-campaign-code') === 'BENJAMINS' &&
      badge.textContent?.trim() && Shopify.currency.active === 'USD' &&
      applied?.status === 'success' && this.#getSessionDiscountCode(applied) === 'BENJAMINS' &&
      (!requested || requested === 'BENJAMINS') && applied.displayPricing === undefined);
  }

  /**
   * Add/Remove free shipping badge
   * @param {Element | null} element
   */
  addFreeShippingBadge(element) {
    if (!element) return;

    const discountsData = this.#purchaseDiscount();

    if (!discountsData) {
      this.#removeFreeShippingBadge(element);
      return;
    }

    const variantId = element.getAttribute('data-variant-id');
    const freeShipping = Theme.legacyBox.discounts.free_shippings.find(
      (f) => f.discount === discountsData.code && f.variant_id === variantId
    );

    if (freeShipping) {
      const freeShippingEl =
        element.querySelector('.free-shipping-badge') ?? document.createElement('div');
      if (freeShippingEl.className !== 'free-shipping-badge') freeShippingEl.className = 'free-shipping-badge';
      if (freeShippingEl.innerText !== freeShipping.text) freeShippingEl.innerText = freeShipping.text;
      if (!element.querySelector('.free-shipping-badge')) element.append(freeShippingEl);
      return;
    }

    this.#removeFreeShippingBadge(element);
  }

  /**
   * Remove discount price
   * @param {Element} element
   */
  #removeFreeShippingBadge(element) {
    element.querySelector('.free-shipping-badge')?.remove();
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
